import React, { useState, useContext, useMemo } from 'react';
import { Box, Grid, Text, ResponsiveContext, ThemeContext } from 'grommet';
import { getInvoiceStatus } from '../../../../utils/business';
import { getInvoiceStatusBadge, getOverdueRange } from '../../../../utils/invoice';
import { formatCurrency } from '../../../../utils/numbers';
import { formatDate } from '../../../../utils/dates';
import { sentencecase } from '../../../../utils/string';
import InvoiceTag from '../../../InvoiceTag';
import CurrencyInput from '../../../CurrencyInput';
import Button from '../../../Button';
import Loading from '../../../Loading';
import MessageBanner from '../../../MessageBanner';
import FontAwesomeIcon from '../../../FontAwesomeIcon';
import {
    faXmarkLarge as IconClose,
    faTriangleExclamation as IconWarning,
    faCircleCheck as IconCheck,
} from '@fortawesome/pro-light-svg-icons';

const TallyLine = ({ title, content, contentProps, ...props }) => (
    <Box justify="between" align="center" direction="row" gap="medium" {...props}>
        <Text>{title}</Text>
        <Text textAlign="end" {...contentProps}>{content}</Text>
    </Box>
);

const AllocateStep = ({
    invoices: initialInvoices,
    unfoundItems = [],
    amountToMatch,
    journalType = 'payment',
    onBack,
    onConfirm,
    isSubmitting,
}) => {
    const theme = useContext(ThemeContext);
    const size = useContext(ResponsiveContext);
    const isMobile = size === 'small';

    const [invoices, setInvoices] = useState(() =>
        initialInvoices.map(inv => {
            const openBalance = parseFloat(inv.open_balance);
            return {
                ...inv,
                open_balance: openBalance,
                amount: parseFloat(inv.amount),
                applied: inv.applied != null ? inv.applied : openBalance,
            };
        })
    );

    const appliedAmount = useMemo(
        () => invoices.reduce((sum, inv) => sum + (inv.applied || 0), 0),
        [invoices]
    );

    const remaining = (amountToMatch || 0) - appliedAmount;

    const paidInvoices = invoices.filter(inv => {
        const status = getInvoiceStatus(inv.due_date, inv.open_balance);
        return status === 'Closed';
    });

    const remainingColor = remaining === 0
        ? 'green-strong'
        : remaining < 0
            ? 'status-critical-2'
            : 'text';

    const handleAppliedChange = (invoiceId, newValue) => {
        setInvoices(prev => prev.map(inv =>
            inv.id === invoiceId ? { ...inv, applied: newValue } : inv
        ));
    };

    const handleRemove = (invoiceId) => {
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
    };

    const canConfirm = invoices.length > 0 && !isSubmitting;

    const gridColumns = isMobile ? ['flex'] : ['flex', '250px'];
    const gridRows = ['auto', 'flex', 'auto'];
    const gridAreas = isMobile
        ? [
            { name: 'warnings', start: [0, 0], end: [0, 0] },
            { name: 'table', start: [0, 1], end: [0, 1] },
            { name: 'footer', start: [0, 2], end: [0, 2] },
        ]
        : [
            { name: 'warnings', start: [0, 0], end: [1, 0] },
            { name: 'table', start: [0, 1], end: [0, 1] },
            { name: 'summary', start: [1, 1], end: [1, 1] },
            { name: 'footer', start: [0, 2], end: [1, 2] },
        ];

    return (
        <Grid
            columns={gridColumns}
            rows={gridRows}
            areas={gridAreas}
            gap="small"
            style={{ height: 'calc(100vh - 80px)' }}
        >
            {/* Warnings */}
            <Box gridArea="warnings" gap="small">
                {unfoundItems.length > 0 && (
                    <MessageBanner
                        compact
                        icon={IconWarning}
                        color="status-warning"
                        message={`${unfoundItems.length} remittance item${unfoundItems.length > 1 ? 's were' : ' was'} not matched.`}
                        ctaText="Back to resolve"
                        onClick={onBack}
                    />
                )}

                {paidInvoices.length > 0 && (
                    <MessageBanner
                        compact
                        icon={IconWarning}
                        color="status-warning"
                        message={`${paidInvoices.length} invoice${paidInvoices.length > 1 ? 's are' : ' is'} already paid`}
                    />
                )}

                {remaining !== 0 && invoices.length > 0 && (
                    <MessageBanner
                        compact
                        icon={IconWarning}
                        color={remaining < 0 ? 'status-critical-2' : 'status-warning'}
                        message="Payment amount does not match the sum of allocated amounts"
                    />
                )}
            </Box>

            {/* Invoice table */}
            <Box gridArea="table" overflow={{ vertical: 'auto' }}>
                {/* Header */}
                {!isMobile && (
                    <Box
                        direction="row"
                        align="center"
                        pad={{ vertical: 'xxsmall', horizontal: 'xsmall' }}
                        border={{ side: 'bottom', color: 'border' }}
                        background="background-contrast"
                        flex={{ shrink: 0 }}
                    >
                        <Box width="25%" flex={{ shrink: 0 }}><Text size="small" weight={500}>Invoice</Text></Box>
                        <Box width="15%" flex={{ shrink: 0 }}><Text size="small" weight={500}>Issued</Text></Box>
                        <Box width="15%" flex={{ shrink: 0 }}><Text size="small" weight={500}>Status</Text></Box>
                        <Box width="15%" flex={{ shrink: 0 }} align="end"><Text size="small" weight={500}>Balance Due</Text></Box>
                        <Box width="25%" flex={{ shrink: 0 }} align="end" pad={{ right: 'xsmall' }}>
                            <Text size="small" weight={500}>Apply</Text>
                        </Box>
                        <Box width="28px" />
                    </Box>
                )}

                {/* Rows */}
                {invoices.map(invoice => (
                    <InvoiceAllocationRow
                        key={invoice.id}
                        invoice={invoice}
                        isMobile={isMobile}
                        theme={theme}
                        onAppliedChange={handleAppliedChange}
                        onRemove={handleRemove}
                    />
                ))}

                {invoices.length === 0 && (
                    <Box pad="medium" align="center">
                        <Text color="text-weak">No invoices selected. Go back to add invoices.</Text>
                    </Box>
                )}
            </Box>

            {/* Summary panel */}
            {!isMobile && (
                <Box
                    gridArea="summary"
                    gap="xsmall"
                    pad="small"
                    border={{ color: 'border' }}
                    round="xsmall"
                    height={{ min: 'auto' }}
                    style={{ position: 'sticky', top: 0, alignSelf: 'start' }}
                >
                    <TallyLine title={sentencecase(journalType)} content={formatCurrency(amountToMatch || 0)} />
                    <TallyLine title="Applied" content={formatCurrency(appliedAmount || 0)} />
                    <TallyLine
                        title="Remaining"
                        contentProps={{ color: remainingColor, weight: 500 }}
                        content={
                            <Box direction="row" gap="xsmall" align="center">
                                {remaining === 0 && invoices.length > 0 && (
                                    <FontAwesomeIcon SelectedIcon={IconCheck} size="small" color="green-strong" />
                                )}
                                <Text color={remainingColor} weight={500}>{formatCurrency(remaining)}</Text>
                            </Box>
                        }
                        pad={{ top: 'small' }}
                        border={{ side: 'top' }}
                    />
                </Box>
            )}

            {/* Mobile summary (inline) */}
            {isMobile && (
                <Box
                    gap="xsmall"
                    pad="small"
                    border={{ color: 'border' }}
                    round="xsmall"
                >
                    <TallyLine title={sentencecase(journalType)} content={formatCurrency(amountToMatch || 0)} />
                    <TallyLine title="Applied" content={formatCurrency(appliedAmount || 0)} />
                    <TallyLine
                        title="Remaining"
                        contentProps={{ color: remainingColor, weight: 500 }}
                        content={
                            <Box direction="row" gap="xsmall" align="center">
                                {remaining === 0 && invoices.length > 0 && (
                                    <FontAwesomeIcon SelectedIcon={IconCheck} size="small" color="green-strong" />
                                )}
                                <Text color={remainingColor} weight={500}>{formatCurrency(remaining)}</Text>
                            </Box>
                        }
                        pad={{ top: 'small' }}
                        border={{ side: 'top' }}
                    />
                </Box>
            )}

            {/* Footer — spans full width */}
            <Box
                gridArea="footer"
                direction="row"
                justify="between"
                align="center"
                pad={{ top: 'small' }}
                border={{ side: 'top' }}
            >
                <Button
                    event={{ section: 'allocate_step', element: 'back' }}
                    secondary
                    onClick={onBack}
                    label="Back"
                />
                <Button
                    event={{ section: 'allocate_step', element: 'confirm' }}
                    primary
                    disabled={!canConfirm}
                    onClick={() => onConfirm(invoices)}
                    label={isSubmitting ? <Loading plain /> : 'Confirm'}
                />
            </Box>
        </Grid>
    );
};

const InvoiceAllocationRow = ({ invoice, isMobile, theme, onAppliedChange, onRemove }) => {
    const [value, setValue] = useState(invoice.applied || 0);
    const invoiceStatus = getInvoiceStatus(invoice.due_date, invoice.open_balance);
    const isPaid = invoiceStatus === 'Closed';

    const onChange = (event) => {
        setValue(parseFloat(event.target.value));
    };

    const onBlur = () => {
        onAppliedChange(invoice.id, value);
    };

    if (isMobile) {
        return (
            <Box
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                border={{ side: 'bottom', color: 'border-weak' }}
                background={isPaid ? { color: 'status-warning', opacity: 'weak' } : undefined}
                flex={{ shrink: 0 }}
                gap="xxsmall"
            >
                <Box direction="row" justify="between" align="center">
                    <InvoiceTag id={invoice.id} />
                    <Button
                        event={{ section: 'allocate_step', element: 'remove_invoice' }}
                        plain
                        onClick={() => onRemove(invoice.id)}
                        label={<FontAwesomeIcon SelectedIcon={IconClose} size="xxsmall" color="text-weak" />}
                    />
                </Box>
                <Box direction="row" justify="between" align="center">
                    <Text size="small" color="text-weak">
                        {invoice.amount === invoice.open_balance
                            ? `${formatCurrency(invoice.open_balance, invoice.currency)} due`
                            : `${formatCurrency(invoice.amount, invoice.currency)} total · ${formatCurrency(invoice.open_balance, invoice.currency)} due`
                        }
                    </Text>
                    <Box border={{ side: 'bottom', color: 'border-xstrong' }} width="110px" overflow="hidden">
                        <CurrencyInput
                            name="applied"
                            ariaLabel="Applied amount"
                            useMinorCurrency={true}
                            value={value}
                            onBlur={onBlur}
                            onChange={onChange}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            direction="row"
            align="center"
            pad={{ horizontal: 'xsmall' }}
            border={{ side: 'bottom', color: 'border-weak' }}
            background={isPaid ? { color: 'status-warning', opacity: 'weak' } : undefined}
            flex={{ shrink: 0 }}
        >
            <Box width="25%" flex={{ shrink: 0 }}><InvoiceTag id={invoice.id} /></Box>
            <Box width="15%" flex={{ shrink: 0 }}><Text>{formatDate(invoice.issued_at)}</Text></Box>
            <Box width="15%" flex={{ shrink: 0 }}>
                {getInvoiceStatusBadge({
                    ...invoice,
                    invoice_status: invoiceStatus,
                    overdue_range: getOverdueRange(new Date(invoice.due_date)),
                    theme,
                })}
            </Box>
            <Box width="15%" flex={{ shrink: 0 }} align="end">
                <Text>{formatCurrency(invoice.open_balance, invoice.currency)}</Text>
            </Box>
            <Box width="25%" flex={{ shrink: 0 }} align="end">
                <Box border={{ side: 'bottom', color: 'border-xstrong' }} width="130px" overflow="hidden">
                    <CurrencyInput
                        name="applied"
                        ariaLabel="Applied amount"
                        useMinorCurrency={true}
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                    />
                </Box>
            </Box>
            <Box width="28px" align="center">
                <Button
                    event={{ section: 'allocate_step', element: 'remove_invoice' }}
                    plain
                    onClick={() => onRemove(invoice.id)}
                    label={<FontAwesomeIcon SelectedIcon={IconClose} size="xxsmall" color="text-weak" />}
                    hoverIndicator
                />
            </Box>
        </Box>
    );
};

export default AllocateStep;
